import { useCallback, useEffect, useState } from "react";
import { ArrowLeftToLine, ArrowRightToLine, LogOut, UserIcon } from "lucide-react";
import Accordion from "./Accordion";
import {
  Accordion as AccordionBase
} from '@/common/components/ui/accordion';
import routes, { Route } from "./routes";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "./Button";
import { useMenu } from "@/common/stores/menu";
import { twMerge } from "tailwind-merge";
import IconButton from "@/common/components/IconButton";
import useLogOut from "@/common/hooks/useLogOut";
import { useCurrentUser } from "@/common/hooks/useCurrentUser";
import { useAppDetails } from "@/common/hooks/useAppDetails";
import LoadingSidebar from "./LoadingSidebar";
import useOnlineStatus from "@/common/hooks/useOnlineStatus";
import QuickSearch from "../QuickSearch";
import { NotificationPopover } from "@/common/components/NotificationPopover";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import useNewReleaseAvailable from "@/common/hooks/useNewReleaseAvailable";
import RenewSession from "../RenewSession";
import { useMineCompany } from "@/features/company/hooks/useMineCompany";

const Sidebar = () => {
  const location = useLocation();
  const { collapse, updateCollapse } = useMenu();
  const [accordionValue, setAccordionValue] = useState<string>('');

  const navigate = useNavigate();

  const { isOnline } = useOnlineStatus();
  const { hasNewReleaseNote } = useNewReleaseAvailable();

  const { commitSha } = useAppDetails()

  const { data: user, isLoading } = useCurrentUser({ staleTime: 1000 * 60 * 60 * 24 });
  const { data: company, isLoading: isMineCompanyLoading } = useMineCompany();
  const cmModuleEnabled = company?.cm_module_enabled ?? false;
  const tmaisModuleEnabled = company?.tmais_module_enabled ?? false;

  const { userPermissionsSet } = useUserPermissions();

  const { logOut, isPending } = useLogOut();

  const isActiveRoute = useCallback((path: string, verifyFullRoute?: boolean) => {
    if (verifyFullRoute) {
      return location.pathname?.toLowerCase().trim() === path?.toLowerCase().trim();
    }

    const currentPath = location.pathname.split('/')[1]?.toLowerCase().trim();
    const targetPath = path.split('/')[1]?.toLowerCase().trim();

    return currentPath === targetPath;
  }, [location.pathname]);

  useEffect(() => {
    if (user && userPermissionsSet.size === 0) {
      navigate('/access-denied', { replace: true })
    }
  }, [userPermissionsSet.size, navigate])

  const isModuleEnabledForRoute = useCallback((route: Route) => {
    if (route.cmModuleShouldBeEnabled === false || route.cmModuleShouldBeEnabled === true && !cmModuleEnabled) {
      return false;
    }

    if (route.tmaisModuleShouldBeEnabled === false || route.tmaisModuleShouldBeEnabled === true && !tmaisModuleEnabled) {
      return false;
    }

    return true;
  }, [cmModuleEnabled, tmaisModuleEnabled]);

  if (isLoading || isPending || isMineCompanyLoading) {
    return <LoadingSidebar />
  }

  return (
    <div
      className={twMerge(
        "relative h-screen w-[283px] left-0 bg-white-200 shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.2)] transition-all duration-150",
        collapse && 'w-[88px]',
        hasNewReleaseNote && 'max-h-[calc(100vh-36px)]'
      )}
    >
      <div className="flex flex-col gap-4">
        {collapse && (
          <div className={twMerge('flex flex-col gap-4 my-2 items-center', !isOnline && 'mt-10')}>
            <IconButton Icon={<ArrowRightToLine strokeWidth={1} />} onClick={() => updateCollapse(false)} />
            <img src="/ReducedLogo.svg" className="w-[18px] h-auto" />

            <NotificationPopover />

            <QuickSearch collapsed />
          </div>
        )}

        {!collapse && (
          <div className={twMerge('flex flex-col gap-4', !isOnline && 'mt-5')}>
            <div className="flex justify-between pt-6 px-6">
              <img src="/Logo.svg" className="w-[83px] h-auto" />

              <NotificationPopover />

              <IconButton Icon={<ArrowLeftToLine strokeWidth={1} />} onClick={() => updateCollapse(true)} />
            </div>

            <div className="px-6">
              <QuickSearch />
            </div>
          </div>
        )}
        <AccordionBase type="single" collapsible onValueChange={(value) => setAccordionValue(value)}>
          <div
            className={twMerge(
              'h-[calc(100vh-289px)] shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.1)_inset] overflow-scroll no-scrollbar',
              collapse && 'flex flex-col items-center gap-4 max-h-[calc(100vh-376px)] pt-4',
              !isOnline && !collapse && 'h-[calc(100vh-290px)]',
              !isOnline && collapse && 'h-[calc(100vh-410px)]',
              hasNewReleaseNote && !collapse && 'h-[calc(100vh-301px)]',
              hasNewReleaseNote && collapse && 'h-[calc(100vh-412px)]'
            )}
          >
            {routes
              .filter((route) => {
                if (!isModuleEnabledForRoute(route)) {
                  return false;
                }

                return typeof route.path !== 'string'
                  ? route.path.some(({ permission }) => userPermissionsSet.has(permission!))
                  : !!route.permission && userPermissionsSet.has(route.permission);
              })
              .map((route, idx) => {
                if (typeof route.path !== 'string') {
                  if (collapse) {
                    return (
                      <Link to={route.path[0].path as string} key={route.path[0].path as string}>
                        <Button
                          Icon={route.Icon}
                          label={route.label}
                          active={!!route.path.find(({ path, verifyFullRouteToActive }) => isActiveRoute(path as string, verifyFullRouteToActive))}
                        />
                      </Link>
                    )
                  }
                  return (
                    <Accordion
                      key={idx}
                      opened={accordionValue === `${idx}`}
                      selected={!!route.path.find(({ path, verifyFullRouteToActive }) => isActiveRoute(path as string, verifyFullRouteToActive))}
                      value={`${idx}`}
                      head={
                        <Button
                          key={`${idx}`}
                          Icon={route.Icon}
                          label={route.label}
                          active={false}
                          showDot={!!route.path.find(({ path, verifyFullRouteToActive }) => isActiveRoute(path as string, verifyFullRouteToActive))}
                          accordion
                        />
                      }
                      body={
                        <div>
                          {route.path
                            .filter(
                              (innerRoute) =>
                                innerRoute.permission &&
                                userPermissionsSet.size > 0 &&
                                userPermissionsSet.has(innerRoute.permission),
                            )
                            .map((innerRoute) => (
                              <Link to={innerRoute.path as string} key={innerRoute.path as string}>
                                <Button
                                  Icon={innerRoute.Icon}
                                  label={innerRoute.label}
                                  active={isActiveRoute(innerRoute.path as string, innerRoute.verifyFullRouteToActive)}
                                />
                              </Link>
                            ))}
                        </div>
                      }
                    />
                  )
                }
                return (
                  <Link to={route.path} key={route.path}>
                    <Button key={route.path} Icon={route.Icon} label={route.label} active={isActiveRoute(route.path, route.verifyFullRouteToActive)} />
                  </Link>
                )
              })}
          </div>
        </AccordionBase>

      </div>
      <div className={twMerge("absolute w-full bottom-0 p-4 flex flex-col gap-4 bg-white-200 shadow-[4px_0px_16px_0px_rgba(0,0,0,0.5)]", collapse && 'items-center')}>
        <div className="flex flex-col gap-2">
          {!collapse && (
            <div className="flex items-center justify-between">
              <p className="font-light text-[10px] italic">
                Versão: <span className="font-bold not-italic">{commitSha}</span>
              </p>

              <RenewSession />
            </div>
          )}


          {collapse
            ? (
              <img src="/ReducedAvantsoft.svg" className="h-[20px]" />
            )
            : (
              <div className="flex items-end justify-between">
                <div className="flex flex-col items-start">
                  <span className="text-[9px] italic font-light">Desenvolvido por</span>
                  <img src='/Avantsoft.svg' className="h-[12px]" />
                </div>

              </div>
            )
          }
        </div>

        <div className={twMerge("flex justify-between items-center", collapse && 'flex-col gap-4')}>
          <div className="flex gap-2 items-center">
            {!user?.profile_picture?.url ? (
              <div className="flex justify-center items-center size-8 bg-white-100 rounded-full">
                <UserIcon />
              </div>
            ) : (
              <img src={user?.profile_picture?.url} className="rounded-full size-8" />
            )}
            {!collapse && <div className="flex flex-col">
              <p data-testid="current_user_name" className="text-sm font-bold max-w-40 text-ellipsis text-nowrap overflow-hidden">
                {user?.full_name}
              </p>
              <Link to="/users/me">
                <span className="italic font-light text-xs cursor-pointer">Configurações de perfil</span>
              </Link>
            </div>}
          </div>
          <IconButton
            Icon={<LogOut color="#FBBCB6" />}
            onClick={() => logOut()}
          />
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
